import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { TranslocoPipe } from '@jsverse/transloco';
import { type CategoryCardItem, SkCategoryCardGridComponent } from '@mercadoni/stark-ui';

import { provideFeatureScope } from '../../../core/services/i18n/i18n-scope';
import { NfDialogComponent } from '../../../shared/components/nf-dialog/nf-dialog.component';
import { scopedTranslate } from '../shared/scoped-translate.util';

/** Catalog scope of this screen; its keys start with `adminAdminHubModal`. */
const SCOPE = 'admin/admin-hub-modal';

/** Id, icon and tint of each launcher card, in display order. */
const CARD_STYLES = [
  { id: 'tool-management', icon: 'wrench', color: 'primary', alpha: 14 },
  { id: 'category-management', icon: 'layers', color: 'info', alpha: 14 },
  { id: 'client-control', icon: 'shield-check', color: 'warning', alpha: 16 },
  { id: 'toolbelt-schema-management', icon: 'file-code', color: 'success', alpha: 14 },
  { id: 'schema-change-requests', icon: 'git-branch', color: 'info', alpha: 14 },
  { id: 'role-management', icon: 'shield', color: 'error', alpha: 14 },
  { id: 'user-management', icon: 'users', color: 'primary', alpha: 14 },
] as const;

/** Launcher dialog listing the admin screens as cards; it has no footer because every action is a card. */
@Component({
  selector: 'nf-admin-hub-modal',
  imports: [NfDialogComponent, SkCategoryCardGridComponent, TranslocoPipe],
  providers: [provideFeatureScope(SCOPE)],
  templateUrl: './admin-hub-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminHubModalComponent {
  readonly isOpen = input<boolean>(false);
  readonly cardSelect = output<string>();
  readonly closeRequested = output<void>();

  private readonly cardTexts = {
    'tool-management': {
      title: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.toolManagement.title'),
      subtitle: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.toolManagement.subtitle'),
    },
    'category-management': {
      title: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.categoryManagement.title'),
      subtitle: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.categoryManagement.subtitle'),
    },
    'client-control': {
      title: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.clientControl.title'),
      subtitle: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.clientControl.subtitle'),
    },
    'toolbelt-schema-management': {
      title: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.toolbeltSchemas.title'),
      subtitle: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.toolbeltSchemas.subtitle'),
    },
    'schema-change-requests': {
      title: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.schemaChangeRequests.title'),
      subtitle: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.schemaChangeRequests.subtitle'),
    },
    'role-management': {
      title: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.roleManagement.title'),
      subtitle: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.roleManagement.subtitle'),
    },
    'user-management': {
      title: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.userManagement.title'),
      subtitle: scopedTranslate(SCOPE, 'adminAdminHubModal.cards.userManagement.subtitle'),
    },
  } as const;

  /** Cards in launcher order; their texts follow the active language. */
  readonly cards = computed((): CategoryCardItem[] =>
    CARD_STYLES.map(({ id, icon, color, alpha }): CategoryCardItem => ({
      id,
      title: this.cardTexts[id].title(),
      subtitle: this.cardTexts[id].subtitle(),
      icon,
      href: '',
      iconBgColor: `color-mix(in srgb, var(--sk-color-${color}-500) ${alpha}%, transparent)`,
      iconColor: `var(--sk-color-${color}-500)`,
    })),
  );

  onCardClick(card: CategoryCardItem): void {
    this.cardSelect.emit(card.id);
  }

  onCloseRequested(): void {
    this.closeRequested.emit();
  }
}
